'use client'

import { useAuthorization } from '@entities/authentication'
import { useCheckCouponForPayment } from '@entities/coupon'
import { isKrOrderEditable, type KrPaymentOrder } from '@entities/kr-payment/model'
import { PaymentMethodType } from '@entities/payment-methods'
import type { PaymentExpectation } from '@entities/payment/api/payment.mutation'
import { userEntityQueries } from '@entities/users'
import { useRegisterDeliveryInformationFormState } from '@features/delivery'
import {
  useCheckPaymentStatusPolling,
  usePaymentByCreditCard,
  usePaymentByKakaoPay,
  usePaymentByNaverPay,
  usePaymentByTossPayments,
  usePaymentQueue,
  usePaymentRedirectedSearchParams,
  usePaymentValidation,
} from '@features/payment'
import { useRegisterCardFormState } from '@features/payment-methods'
import { PromotionType, SubscribeTicket } from '@server/schemas'
import { env } from '@shared/config/env'
import { useTranslation } from '@shared/config/i18n'
import { sendGA4PurchaseEvent, success, withAsyncPipeline, withSuccessCallback } from '@shared/libs'
import { useNativeBridgeStore } from '@shared/models'
import { openAlertDialog } from '@shared/ui'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { overlay } from 'overlay-kit'
import { useMemo, useRef } from 'react'
import { match } from 'ts-pattern'

import { useKrOrderShipping } from './use-kr-order-shipping'
import { useKrPreparedPayment } from './use-kr-prepared-payment'

type PaymentInfo = PaymentExpectation & {
  isTermsAgreed: boolean
  requiresTermsAgreement?: boolean
  paymentMethodType: Nullable<PaymentMethodType>
  customerUid: Nullable<string>
  amount: number
  couponId: Nullable<string>
  subscribeTicket: SubscribeTicket
  promotionType?: PromotionType | null
  productId?: string | null
  noticeVersionId?: string | null
  tutorConnectId?: string | null
  productName?: string | null
  currency?: string
  krPreparedCheckout?: boolean
  preparedOrderStatus?: KrPaymentOrder['status']
  preparedOrderCanReplace?: boolean
  preparedOrderAuthority?: KrPaymentOrder['authorityMode']
  preparedOrderShipping?: KrPaymentOrder['shipping']
  preparedOrderId?: string
  onPreparationPriceRejected?: () => Promise<void>
}

const formatPaymentAmount = (value: number, currency = 'KRW') => {
  const code = currency.toUpperCase()
  if (code === 'KRW') {
    return `₩${value.toLocaleString()}`
  }
  if (code === 'JPY') {
    return `¥${value.toLocaleString()}`
  }
  if (code === 'TWD') {
    return `NT$${value.toLocaleString()}`
  }
  if (code === 'USD') {
    return `$${value.toLocaleString()}`
  }
  return `${value.toLocaleString()} ${code}`
}

export const usePayment = ({
  isTermsAgreed,
  requiresTermsAgreement = true,
  paymentMethodType,
  customerUid,
  amount,
  couponId,
  subscribeTicket,
  promotionType,
  productId,
  noticeVersionId,
  tutorConnectId,
  productName,
  currency = 'KRW',
  krPreparedCheckout = false,
  preparedOrderStatus,
  preparedOrderCanReplace,
  preparedOrderAuthority,
  preparedOrderShipping,
  preparedOrderId,
  expectedSalePrice,
  expectedTotalLessonCount,
  expectedValidityMonths,
  onPreparationPriceRejected,
}: PaymentInfo) => {
  const t = useTranslation()
  const router = useRouter()
  const authorization = useAuthorization()
  const nativeStore = useNativeBridgeStore()
  const { data: userInfo } = useSuspenseQuery({
    ...userEntityQueries.getCurrentUser({ bearerToken: authorization.bearerToken }),
  })
  const checkCouponForPaymentMutation = useCheckCouponForPayment({
    couponId,
    subscribeId: subscribeTicket.id,
    productId,
  })

  const { formState: registerCardFormState } = useRegisterCardFormState()
  const { formState: registerDeliveryInformationFormState } = useRegisterDeliveryInformationFormState()
  const shipping = useKrOrderShipping({
    enabled: krPreparedCheckout && subscribeTicket.paymentType === 'IPAD',
    orderId: preparedOrderId,
    shipping: preparedOrderShipping,
    status: preparedOrderStatus,
    canReplace: preparedOrderCanReplace,
  })

  const billingFailSlackMessage = (errorCode?: string, errorMessage?: string) => {
    return `[Payment API failed] Product: ${productName ?? `${subscribeTicket.lessonMonth}-month lesson package`} / User: ${userInfo.name}(${userInfo.id}) / Amount: ${formatPaymentAmount(amount, currency)}
[${errorCode}] ${errorMessage}

\`System info\`
  - \`OS\` : ${nativeStore.os}
  - \`UserAgent\` : ${nativeStore.userAgent}
  - \`RuntimeVersion\` : ${nativeStore.runtimeVersion}
  - \`from\` : ${nativeStore.from}`
  }

  const createNotificationWebhookUrl = (): URL => {
    const webhookUrl = new URL(`${env.NEXT_PUBLIC_PODO_API_URL}/api/v1/payment/podo/webhook`)

    webhookUrl.searchParams.set('user_id', authorization.uid.toString())
    webhookUrl.searchParams.set('subscribe_id', subscribeTicket.id)
    if (productId) {
      webhookUrl.searchParams.set('product_id', productId)
    }
    if (noticeVersionId) {
      webhookUrl.searchParams.set('notice_version_id', noticeVersionId)
    }
    if (tutorConnectId) {
      webhookUrl.searchParams.set('tutor_connect_id', tutorConnectId)
    }

    if (couponId) {
      webhookUrl.searchParams.set('coupon_id', couponId)
    }

    for (const [key, value] of Object.entries({
      expected_sale_price: expectedSalePrice,
      expected_total_lesson_count: expectedTotalLessonCount,
      expected_validity_months: expectedValidityMonths,
    })) {
      if (value !== undefined) {
        webhookUrl.searchParams.set(key, String(value))
      }
    }

    // payment_type 설정 (백엔드는 IPAD를 LUMP_SUM으로 처리)
    const paymentType = match(subscribeTicket.paymentType)
      .with('SUBSCRIBE', () => 'FIRST_BILLING')
      .with('LUMP_SUM', () => 'LUMP_SUM')
      .with('IPAD', () => 'LUMP_SUM')
      .otherwise(() => 'FIRST_BILLING')
    webhookUrl.searchParams.set('payment_type', paymentType)

    // IPAD 결제 시 배송 정보 추가
    if (subscribeTicket.paymentType === 'IPAD' && !krPreparedCheckout) {
      webhookUrl.searchParams.set('order_name', userInfo.name)
      webhookUrl.searchParams.set(
        'order_address',
        `${registerDeliveryInformationFormState.values.address} ${registerDeliveryInformationFormState.values.detailAddress}`,
      )
      webhookUrl.searchParams.set('order_phone', userInfo.phone)
      webhookUrl.searchParams.set('order_zip_code', registerDeliveryInformationFormState.values.zipCode)
    }

    return webhookUrl
  }

  const redirectedSearchParams = usePaymentRedirectedSearchParams()
  const redirectedAmountParam = redirectedSearchParams?.amount
  const gaAmount =
    redirectedAmountParam !== undefined && redirectedAmountParam !== null ? Number(redirectedAmountParam) : amount

  const transactionIdRef = useRef<string>(`T_${Date.now()}${Math.random()}`)

  const sendPurchaseEvent = (): 'success' | 'error' => {
    const payload = {
      transactionId: transactionIdRef.current,
      value: gaAmount,
      currency,
      items: [
        {
          itemId: productName ?? subscribeTicket.subName,
          itemName: productName ?? subscribeTicket.subName,
          itemCategory: 'Package',
          price: gaAmount,
          quantity: 1,
        },
      ],
      userId: userInfo.id,
      userName: userInfo.name,
      userPhone: userInfo.phone,
      userEmail: userInfo.email,
    }

    sendGA4PurchaseEvent(payload)

    // 성공 페이지에서 동일 파라미터로 재전송할 수 있도록 세션에 보관
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem('PODO_GA_PURCHASE_PAYLOAD', JSON.stringify(payload))
      } catch {
        void 0
      }
    }

    return success()
  }

  const hasSentPurchaseEventRef = useRef(false)
  const sendPurchaseEventOnce = (): 'success' | 'error' => {
    if (hasSentPurchaseEventRef.current) {
      return success()
    }
    hasSentPurchaseEventRef.current = true
    return sendPurchaseEvent()
  }

  const { pollingState, setPollingState } = useCheckPaymentStatusPolling({
    onSuccess: async (extras) => {
      if (krPreparedCheckout) {
        sessionStorage.removeItem(`podo:kr-purchase:${authorization.uid}:${subscribeTicket.id}`)
      }
      // 리다이렉트 전에 구매 이벤트 전송
      sendPurchaseEventOnce()

      // Small delay to improve delivery before navigation
      await new Promise((resolve) => setTimeout(resolve, 300))

      overlay.closeAll()
      const curriculumTypeParam = subscribeTicket.curriculumType
      const searchParams = new URLSearchParams()
      if (curriculumTypeParam) {
        searchParams.set('curriculumType', curriculumTypeParam)
      }
      searchParams.set('amount', gaAmount.toString())
      searchParams.set('currency', currency)
      if (productName) {
        searchParams.set('productName', productName)
      }
      if (productId) {
        searchParams.set('productId', productId)
      }
      searchParams.set('transactionId', transactionIdRef.current)
      if (promotionType) {
        searchParams.set('promotionType', promotionType)
      }
      if (extras?.isDuplicatePurchase) {
        searchParams.set('isDuplicatePurchase', 'true')
      }
      const search = searchParams.toString()
      router.replace(`/subscribes/payment/${subscribeTicket.id}/success?${search}`)
    },
  })

  const handlePaymentQueue = usePaymentQueue({
    handleEnabledPolling: (merchantUid) =>
      setPollingState({
        status: 'in-progress',
        data: { merchantUid, ...(krPreparedCheckout ? { purchaseOrderId: merchantUid } : {}) },
      }),
  })

  const preparePayment = useKrPreparedPayment({
    enabled: krPreparedCheckout,
    onPreparationPriceRejected,
    subscribeId: subscribeTicket.id,
    productId,
    amount,
    couponId,
    paymentMethod: paymentMethodType,
    isLumpSum: subscribeTicket.paymentType !== 'SUBSCRIBE',
    shipping,
    expectedTotalLessonCount,
    expectedValidityMonths,
    onPending: (merchantUid) =>
      setPollingState({ status: 'in-progress', data: { merchantUid, purchaseOrderId: merchantUid } }),
  })

  const {
    validateClientSideRegisterCard,
    validateServerSideRegisterCard,
    validatePayment: validateBasePayment,
  } = usePaymentValidation({
    isTermsAgreed,
    requiresTermsAgreement,
    paymentMethodType,
    registerCardFormState,
    paymentType: subscribeTicket.paymentType,
    registerDeliveryInformationFormState,
  })

  const validatePayment = (): boolean => {
    if (amount < 0) {
      openAlertDialog({ title: t('payment.alerts.invalidPaymentAmount') })
      return false
    }

    return validateBasePayment()
  }

  const customData = useMemo(
    () => ({
      user_id: authorization.uid.toString(),
      subscribe_id: subscribeTicket.id,
      product_id: productId ?? undefined,
      notice_version_id: noticeVersionId ?? undefined,
      tutor_connect_id: tutorConnectId ?? undefined,
      coupon_id: couponId,
      is_v2_validated: true,
      ...(expectedSalePrice !== undefined ? { expected_sale_price: expectedSalePrice } : {}),
      ...(expectedTotalLessonCount !== undefined ? { expected_total_lesson_count: expectedTotalLessonCount } : {}),
      ...(expectedValidityMonths !== undefined ? { expected_validity_months: expectedValidityMonths } : {}),
    }),
    [
      authorization.uid,
      couponId,
      noticeVersionId,
      productId,
      subscribeTicket.id,
      tutorConnectId,
      expectedSalePrice,
      expectedTotalLessonCount,
      expectedValidityMonths,
    ],
  )

  const checkCouponIfUseCoupon = async () => {
    if (!couponId || krPreparedCheckout) {
      return
    }
    await checkCouponForPaymentMutation.mutateAsync(undefined, {
      onError: () => {
        openAlertDialog({ title: t('payment.alerts.unavailableCoupon') })
      },
    })
  }

  const merchantName = productName ?? subscribeTicket.subName

  const { handlePaymentForNewCreditCard, handlePaymentForRegisteredCreditCard } = usePaymentByCreditCard({
    preparePayment,
    handlePaymentQueue,
    validatePayment,
    validateClientSideRegisterCard,
    validateServerSideRegisterCard,
    customerUid,
    merchantName,
    amount,
    currency,
    customData,
    checkCouponIfUseCoupon,
    paymentType: 'FIRST_BILLING',
    billingFailSlackMessage,
    paymentWebhookUrl: createNotificationWebhookUrl(),
  })

  const { handlePaymentForNewKakaoPay, handlePaymentForRegisteredKakaoPay } = usePaymentByKakaoPay({
    preparePayment,
    validatePayment,
    handlePaymentQueue,
    customerUid,
    merchantName,
    amount,
    currency,
    customData,
    checkCouponIfUseCoupon,
    billingFailSlackMessage,
    paymentType: match(subscribeTicket.paymentType)
      .with('SUBSCRIBE', () => 'FIRST_BILLING' as const)
      .with('LUMP_SUM', () => 'LUMP_SUM' as const)
      .with('IPAD', () => 'LUMP_SUM' as const)
      .otherwise(() => 'FIRST_BILLING' as const),
    couponId: couponId,
    paymentWebhookUrl: createNotificationWebhookUrl(),
    mobileRedirectSearchParams: match(subscribeTicket.paymentType)
      .with('SUBSCRIBE', () => ({
        __brand: 'FIRST_BILLING' as const,
        coupon_id: couponId ?? undefined,
      }))
      .with('LUMP_SUM', () => ({
        __brand: 'LUMP_SUM' as const,
        coupon_id: couponId ?? undefined,
      }))
      .with('IPAD', () => ({
        __brand: 'IPAD' as const,
        zip_code: krPreparedCheckout ? undefined : registerDeliveryInformationFormState.values.zipCode,
        address: krPreparedCheckout ? undefined : registerDeliveryInformationFormState.values.address,
        detail_address: krPreparedCheckout ? undefined : registerDeliveryInformationFormState.values.detailAddress,
        coupon_id: couponId ?? undefined,
      }))
      .otherwise(() => null),
  })

  const { handlePaymentForNewNaverPay, handlePaymentForRegisteredNaverPay } = usePaymentByNaverPay({
    preparePayment,
    validatePayment,
    handlePaymentQueue,
    customerUid,
    merchantName,
    amount,
    currency,
    customData,
    checkCouponIfUseCoupon,
    billingFailSlackMessage,
    paymentType: match(subscribeTicket.paymentType)
      .with('SUBSCRIBE', () => 'FIRST_BILLING' as const)
      .with('LUMP_SUM', () => 'LUMP_SUM' as const)
      .with('IPAD', () => 'LUMP_SUM' as const)
      .otherwise(() => 'FIRST_BILLING' as const),
    couponId: couponId,
    installmentPeriod: subscribeTicket.lessonMonth,
    paymentWebhookUrl: createNotificationWebhookUrl(),
    mobileRedirectSearchParams: match(subscribeTicket.paymentType)
      .with('SUBSCRIBE', () => ({
        __brand: 'FIRST_BILLING' as const,
        coupon_id: couponId ?? undefined,
      }))
      .with('LUMP_SUM', () => ({
        __brand: 'LUMP_SUM' as const,
        coupon_id: couponId ?? undefined,
      }))
      .with('IPAD', () => ({
        __brand: 'IPAD' as const,
        zip_code: krPreparedCheckout ? undefined : registerDeliveryInformationFormState.values.zipCode,
        address: krPreparedCheckout ? undefined : registerDeliveryInformationFormState.values.address,
        detail_address: krPreparedCheckout ? undefined : registerDeliveryInformationFormState.values.detailAddress,
        coupon_id: couponId ?? undefined,
      }))
      .otherwise(() => null),
  })

  const handlePaymentForTossPayments = usePaymentByTossPayments({
    preparePayment,
    validatePayment,
    handlePaymentQueue,
    merchantName,
    amount,
    currency,
    customData,
    checkCouponIfUseCoupon,
    billingFailSlackMessage,
    paymentType: 'LUMP_SUM',
    paymentWebhookUrl: createNotificationWebhookUrl(),
    mobileRedirectSearchParams: match(subscribeTicket.paymentType)
      .with('LUMP_SUM', (lumpsumPaymentType) => ({
        __brand: lumpsumPaymentType,
        coupon_id: couponId ?? undefined,
      }))
      .with('IPAD', (ipadPaymentType) => ({
        __brand: ipadPaymentType,
        zip_code: krPreparedCheckout ? undefined : registerDeliveryInformationFormState.values.zipCode,
        address: krPreparedCheckout ? undefined : registerDeliveryInformationFormState.values.address,
        detail_address: krPreparedCheckout ? undefined : registerDeliveryInformationFormState.values.detailAddress,
        coupon_id: couponId ?? undefined,
      }))
      .otherwise(() => null),
  })

  return {
    resumePreparedPayment:
      krPreparedCheckout &&
      (preparedOrderStatus === 'PAID' ||
        preparedOrderStatus === 'FULFILLMENT_PENDING' ||
        (preparedOrderStatus === 'PROCESSING' &&
          !isKrOrderEditable({ status: preparedOrderStatus, canReplace: preparedOrderCanReplace }) &&
          (subscribeTicket.paymentType === 'SUBSCRIBE' || preparedOrderAuthority === 'MANUAL_CONFIRM_V2')))
        ? async () => {
            await preparePayment?.()
          }
        : undefined,
    handlePaymentForNewCreditCard: withAsyncPipeline(
      handlePaymentForNewCreditCard,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForRegisteredCreditCard: withAsyncPipeline(
      handlePaymentForRegisteredCreditCard,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForNewKakaoPay: withAsyncPipeline(
      handlePaymentForNewKakaoPay,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForRegisteredKakaoPay: withAsyncPipeline(
      handlePaymentForRegisteredKakaoPay,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForNewNaverPay: withAsyncPipeline(
      handlePaymentForNewNaverPay,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForRegisteredNaverPay: withAsyncPipeline(
      handlePaymentForRegisteredNaverPay,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    handlePaymentForTossPayments: withAsyncPipeline(
      handlePaymentForTossPayments,
      withSuccessCallback(() => (krPreparedCheckout ? success() : sendPurchaseEventOnce())),
    ),
    validatePayment,
    pollingState,
  }
}
